import { Step, StepLabel, Stepper } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { INCOME_OR_EXPENSE_LIST } from 'adminConstants/routes';
import ErrorBoundary from 'components/ErrorBoundary';
import Page from 'components/new/Page';
import PageLoader from 'components/PageLoader';
import { Column, Row } from 'further-ui/components/Layout';
import { useLayoutContext } from 'contexts/LayoutContext';
import { IncomeOrExpenseType } from 'further-types/lp/income-and-expenses';
import { FundingSource } from 'further-types/lp/investment';
import { CurrencyCode } from 'further-types/lp/vehicle';
import api from 'lib/trpcClient';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { AiFormFillProvider } from './AiFormFillContext';
import FormWrapper from './FormWrapper';
import InvoicePreview from './InvoicePreview';
import StepAddAndAllocate from './StepAddAndAllocate';
import StepReviewAndConfirm from './StepReviewAndConfirm';

enum Steps {
  AddAndAllocate,
  ReviewAndConfirm,
}

const stepLabels = {
  [Steps.AddAndAllocate]: 'Add and allocate',
  [Steps.ReviewAndConfirm]: 'Review and confirm',
};

type Props = {
  vehicleId: string;
};

const StepperWrapper = styled('div')({
  width: 460,
});

const PageLayout = styled(Row)(({ theme }) => ({
  height: `calc(100vh - ${theme.searchBar.height + 2}px)`,
}));

const PREVIEW_MIN_PCT = 25;
const PREVIEW_MAX_PCT = 50;
const PREVIEW_DEFAULT_PCT = (5 / 12) * 100;

const FormColumn = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'hasPreview',
})<{ hasPreview: boolean }>(({ hasPreview }) => ({
  flex: hasPreview ? '1 1 0%' : '1 1 auto',
  minWidth: 0,
}));

const PreviewColumn = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'previewWidth',
})<{ previewWidth: number }>(({ theme, previewWidth }) => ({
  width: `${previewWidth}%`,
  flexShrink: 0,
  height: `calc(100vh - ${theme.searchBar.height + 2}px)`,
  backgroundColor: theme.palette.grey[200],
  padding: theme.spacing(12),
  position: 'relative',
  '& .preview-drag-icon': {
    opacity: 0,
    transition: 'opacity 0.15s',
  },
  '&:hover .preview-drag-icon': {
    opacity: 1,
  },
}));

const ResizeHandle = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: 4,
  cursor: 'col-resize',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.grey[300],
}));

const ResizeHandleIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.grey[300],
  borderRadius: theme.shape.borderRadius * 2,
  cursor: 'col-resize',
  padding: theme.spacing(1),
  boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
}));

type PreviewData = {
  fileUrl?: string | null;
  fileName?: string | null;
  isUploading: boolean;
} | null;

const CreateFormWrapper: React.FC<{
  vehicleId: string;
  baseCurrency?: CurrencyCode;
  defaultCategory?: IncomeOrExpenseType;
  isSwitchMode?: boolean;
  children: React.ReactNode;
}> = ({ vehicleId, ...props }) => {
  const [generalCapital] = api.capital.getGeneralCapital.useSuspenseQuery({
    vehicleId,
  });
  const defaultFundingSource = generalCapital.hasGeneralCapitalCalls
    ? FundingSource.GeneralCapitalOnAccounts
    : FundingSource.CapitalCall;
  return (
    <FormWrapper
      vehicleId={vehicleId}
      defaultFundingSource={defaultFundingSource}
      {...props}
    />
  );
};

const AddEditIncomeOrExpense: React.FC<Props> = ({ vehicleId }) => {
  const { id, type } = useParams<{ id?: string; type?: IncomeOrExpenseType }>();
  const { vehicle } = useLayoutContext();
  const location = useLocation();
  const isSwitchMode =
    new URLSearchParams(location.search).get('mode') === 'switch';
  const [activeStep, setActiveStep] = useState<Steps>(Steps.AddAndAllocate);
  const [previewData, setPreviewData] = useState<PreviewData>(null);
  const [previewWidth, setPreviewWidth] = useState(PREVIEW_DEFAULT_PCT);
  const [isResizing, setIsResizing] = useState(false);
  const hasPreview = previewData !== null;
  const isDraggingRef = useRef(false);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    // Capture layout bounds once — ResizeHandle → PreviewColumn → PageLayout
    const layout = (e.currentTarget as HTMLElement).parentElement
      ?.parentElement;
    const { right, width } = layout?.getBoundingClientRect() ?? {
      right: window.innerWidth,
      width: window.innerWidth,
    };
    isDraggingRef.current = true;
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const pct = ((right - ev.clientX) / width) * 100;
      setPreviewWidth(
        Math.min(PREVIEW_MAX_PCT, Math.max(PREVIEW_MIN_PCT, pct)),
      );
    };

    const onMouseUp = () => {
      isDraggingRef.current = false;
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const { data: incomeOrExpense, isLoading: incomeOrExpenseLoading } =
    api.incomeAndExpenses.get.useQuery(
      { vehicleId, id: id ?? '' },
      { enabled: !!id },
    );

  // When editing a record that already has an invoice file, show preview immediately
  useEffect(() => {
    if (incomeOrExpense?.invoiceFile) {
      setPreviewData({
        fileUrl: incomeOrExpense.invoiceFile.preview,
        fileName: incomeOrExpense.invoiceFile.name,
        isUploading: false,
      });
    }
  }, [incomeOrExpense?.invoiceFile]);

  if (incomeOrExpenseLoading) {
    return <PageLoader />;
  }

  const title = isSwitchMode
    ? 'Switch to payable or receivable'
    : id
    ? 'Edit income or expense'
    : 'Add new income or expense';

  const stepper = (
    <Stepper activeStep={activeStep}>
      {Object.values(stepLabels).map((stepLabel) => (
        <Step key={stepLabel}>
          <StepLabel>{stepLabel}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );

  const page = (
    <Page
      tabTitle="Income & Expenses: Add Income or Expense"
      heading={title}
      goBackAction={INCOME_OR_EXPENSE_LIST}
      spacing="lg"
      withHeadingDivider
      customActions={
        hasPreview ? undefined : (
          <Row spacing="sm" centered>
            <StepperWrapper>{stepper}</StepperWrapper>
          </Row>
        )
      }
      belowHeading={hasPreview ? stepper : undefined}
    >
      {id ? (
        <FormWrapper
          values={incomeOrExpense}
          vehicleId={vehicleId}
          baseCurrency={vehicle?.baseCurrency}
          defaultCategory={type}
          isSwitchMode={isSwitchMode}
        >
          <AiFormFillProvider>
            {activeStep === Steps.AddAndAllocate && (
              <StepAddAndAllocate
                id={id}
                isSwitchMode={isSwitchMode}
                onPreviewDataChange={setPreviewData}
                onNext={() => setActiveStep(Steps.ReviewAndConfirm)}
              />
            )}
            {activeStep === Steps.ReviewAndConfirm && !!vehicle && (
              <ErrorBoundary>
                <StepReviewAndConfirm
                  id={id}
                  vehicle={vehicle}
                  isSwitchMode={isSwitchMode}
                  onPreviousStep={() => setActiveStep(Steps.AddAndAllocate)}
                />
              </ErrorBoundary>
            )}
          </AiFormFillProvider>
        </FormWrapper>
      ) : (
        <ErrorBoundary loader={<PageLoader />}>
          <CreateFormWrapper
            vehicleId={vehicleId}
            baseCurrency={vehicle?.baseCurrency}
            defaultCategory={type}
            isSwitchMode={isSwitchMode}
          >
            <AiFormFillProvider>
              {activeStep === Steps.AddAndAllocate && (
                <StepAddAndAllocate
                  isSwitchMode={isSwitchMode}
                  onPreviewDataChange={setPreviewData}
                  onNext={() => setActiveStep(Steps.ReviewAndConfirm)}
                />
              )}
              {activeStep === Steps.ReviewAndConfirm && !!vehicle && (
                <ErrorBoundary>
                  <StepReviewAndConfirm
                    vehicle={vehicle}
                    isSwitchMode={isSwitchMode}
                    onPreviousStep={() => setActiveStep(Steps.AddAndAllocate)}
                  />
                </ErrorBoundary>
              )}
            </AiFormFillProvider>
          </CreateFormWrapper>
        </ErrorBoundary>
      )}
    </Page>
  );

  return (
    <PageLayout>
      <FormColumn hasPreview={hasPreview}>{page}</FormColumn>
      {hasPreview && (
        <PreviewColumn previewWidth={previewWidth}>
          <ResizeHandle onMouseDown={handleResizeMouseDown}>
            <ResizeHandleIcon className="preview-drag-icon">
              <DragIndicator
                fontSize="small"
                sx={{ color: 'text.secondary' }}
              />
            </ResizeHandleIcon>
          </ResizeHandle>
          <InvoicePreview
            fileUrl={previewData?.fileUrl}
            fileName={previewData?.fileName}
            isUploading={previewData?.isUploading}
            isResizing={isResizing}
          />
        </PreviewColumn>
      )}
    </PageLayout>
  );
};

const AddEditIncomeOrExpenseWrapper: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();

  if (!selectedVehicleId) {
    return null;
  }

  return <AddEditIncomeOrExpense vehicleId={selectedVehicleId} />;
};

export default AddEditIncomeOrExpenseWrapper;
